import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  LuBold,
  LuItalic,
  LuHeading1,
  LuHeading2,
  LuHeading3,
  LuList,
  LuListOrdered,
  LuLink,
  LuX,
  LuCheck,
} from "react-icons/lu";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkRehype from "remark-rehype";
import rehypeStringify from "rehype-stringify";

/**
 * Parses Markdown into semantic HTML for direct rendering inside the in-place editor.
 * Uses official unified/remark/rehype libraries for standard CommonMark/GFM compliance.
 */
function markdownToHtml(md) {
  if (!md || !md.trim()) return "";
  try {
    const file = unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkRehype)
      .use(rehypeStringify)
      .processSync(md);
    return String(file);
  } catch (err) {
    return md;
  }
}

/**
 * Converts formatted HTML from the in-place editor into clean CommonMark/GFM Markdown.
 * Ensures bold/italic markers are distributed inside list items instead of wrapping bullet dashes.
 */
function htmlToMarkdown(htmlString) {
  if (!htmlString) return "";
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.nodeValue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return "";
    }

    const tag = node.tagName.toLowerCase();
    const children = Array.from(node.childNodes).map(walk).join("");

    switch (tag) {
      case "h1": {
        const lines = children.split("\n");
        return `\n\n${lines
          .map((l) => {
            const clean = l.replace(/^[-*]\s+/, "").replace(/^\d+\.\s+/, "").trim();
            return clean ? `# ${clean}` : "";
          })
          .filter(Boolean)
          .join("\n")}\n\n`;
      }
      case "h2": {
        const lines = children.split("\n");
        return `\n\n${lines
          .map((l) => {
            const clean = l.replace(/^[-*]\s+/, "").replace(/^\d+\.\s+/, "").trim();
            return clean ? `## ${clean}` : "";
          })
          .filter(Boolean)
          .join("\n")}\n\n`;
      }
      case "h3": {
        const lines = children.split("\n");
        return `\n\n${lines
          .map((l) => {
            const clean = l.replace(/^[-*]\s+/, "").replace(/^\d+\.\s+/, "").trim();
            return clean ? `### ${clean}` : "";
          })
          .filter(Boolean)
          .join("\n")}\n\n`;
      }
      case "strong":
      case "b": {
        if (!children || !children.trim()) return "";
        const lines = children.split("\n");
        return lines
          .map((line) => {
            if (!line.trim()) return line;
            const listMatch = line.match(/^(\s*(?:[-*+]|\d+\.)\s+)(.*)$/);
            if (listMatch) {
              return `${listMatch[1]}**${listMatch[2].trim()}**`;
            }
            const bqMatch = line.match(/^(\s*>\s*)(.*)$/);
            if (bqMatch) {
              return `${bqMatch[1]}**${bqMatch[2].trim()}**`;
            }
            const hMatch = line.match(/^(\s*#{1,6}\s+)(.*)$/);
            if (hMatch) {
              return `${hMatch[1]}**${hMatch[2].trim()}**`;
            }
            const leadingSpace = line.match(/^\s*/)[0];
            const trailingSpace = line.match(/\s*$/)[0];
            return `${leadingSpace}**${line.trim()}**${trailingSpace}`;
          })
          .join("\n");
      }
      case "em":
      case "i": {
        if (!children || !children.trim()) return "";
        const lines = children.split("\n");
        return lines
          .map((line) => {
            if (!line.trim()) return line;
            const listMatch = line.match(/^(\s*(?:[-*+]|\d+\.)\s+)(.*)$/);
            if (listMatch) {
              return `${listMatch[1]}*${listMatch[2].trim()}*`;
            }
            const bqMatch = line.match(/^(\s*>\s*)(.*)$/);
            if (bqMatch) {
              return `${bqMatch[1]}*${bqMatch[2].trim()}*`;
            }
            const hMatch = line.match(/^(\s*#{1,6}\s+)(.*)$/);
            if (hMatch) {
              return `${hMatch[1]}*${hMatch[2].trim()}*`;
            }
            const leadingSpace = line.match(/^\s*/)[0];
            const trailingSpace = line.match(/\s*$/)[0];
            return `${leadingSpace}*${line.trim()}*${trailingSpace}`;
          })
          .join("\n");
      }
      case "del":
      case "s":
      case "strike": {
        if (!children || !children.trim()) return "";
        return `~~${children.trim()}~~`;
      }
      case "blockquote": {
        const lines = children.split("\n");
        return `\n\n${lines
          .map((l) => (l.trim() ? `> ${l.trim()}` : ""))
          .filter(Boolean)
          .join("\n")}\n\n`;
      }
      case "a": {
        const href = node.getAttribute("href") || "#";
        const text = children.trim() || href;
        return `[${text}](${href})`;
      }
      case "li": {
        const trimmed = children.trim();
        if (/^#{1,6}\s+/.test(trimmed)) {
          return `${trimmed}\n`;
        }
        const cleanContent = trimmed.replace(/^[-*•]\s+/, "");
        const parentTag = node.parentElement?.tagName.toLowerCase();
        if (parentTag === "ol") {
          const index = Array.from(node.parentElement.children).indexOf(node) + 1;
          return `${index}. ${cleanContent}\n`;
        }
        return `- ${cleanContent}\n`;
      }
      case "ul":
        return `\n\n${children.trim()}\n\n`;
      case "ol":
        return `\n\n${children.trim()}\n\n`;
      case "p":
      case "div":
        return children.trim() ? `\n\n${children.trim()}\n\n` : "\n";
      case "br":
        return "\n";
      default:
        return children;
    }
  }

  const raw = walk(doc.body)
    .replace(/\r\n/g, "\n")
    .replace(/^\s*(\*{1,3})\s*-\s+/gm, "- $1")
    .replace(/(^[^-*\d\s#].*)\n([-*]\s+|\d+\.\s+)/gm, "$1\n\n$2")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  if (raw === "\n" || !raw) return "";
  return raw;
}

/**
 * In-place WYSIWYG Markdown Editor component.
 * Allows typing and applying formatting options (Bold, Italic, Headings, Lists, Quotes, Links)
 * directly in one single surface with immediate visual feedback, without separate preview tabs or split screens.
 */
export default function MarkdownEditor({
  value = "",
  onChange,
  placeholder = "Enter your announcement message...",
  height = 180,
  className = "",
}) {
  const editorRef = useRef(null);
  const isTypingRef = useRef(false);
  const savedSelectionRef = useRef(null);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [isEmpty, setIsEmpty] = useState(!value || !value.trim());

  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    h1: false,
    h2: false,
    h3: false,
    ul: false,
    ol: false,
  });

  // Query formatting state at current cursor position
  const updateActiveFormats = useCallback(() => {
    try {
      const bold = document.queryCommandState("bold");
      const italic = document.queryCommandState("italic");
      const ul = document.queryCommandState("insertUnorderedList");
      const ol = document.queryCommandState("insertOrderedList");

      const blockVal = (document.queryCommandValue("formatBlock") || "").toLowerCase();
      let h1 = blockVal === "h1" || blockVal.includes("h1");
      let h2 = blockVal === "h2" || blockVal.includes("h2");
      let h3 = blockVal === "h3" || blockVal.includes("h3");

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        let node = selection.anchorNode;
        if (node && node.nodeType === Node.TEXT_NODE) {
          node = node.parentElement;
        }
        if (node && editorRef.current && editorRef.current.contains(node)) {
          const headingElem = node.closest("h1, h2, h3");
          if (headingElem) {
            const tag = headingElem.tagName.toLowerCase();
            if (tag === "h1") h1 = true;
            if (tag === "h2") h2 = true;
            if (tag === "h3") h3 = true;
          }
        }
      }

      setActiveFormats({ bold, italic, h1, h2, h3, ul, ol });
    } catch {
      // Ignored
    }
  }, []);

  // Save selection before opening link popup
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    if (savedSelectionRef.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);
    }
  };

  // Sync external value into editor when not actively typing
  useEffect(() => {
    if (isTypingRef.current) return;

    const editor = editorRef.current;
    if (!editor) return;

    const currentMarkdown = htmlToMarkdown(editor.innerHTML);
    if ((value || "").trim() !== currentMarkdown.trim()) {
      editor.innerHTML = markdownToHtml(value || "");
      setIsEmpty(!editor.innerText.trim());
    }
  }, [value]);

  // Execute formatting command directly on the in-place editor
  const executeCommand = useCallback(
    (command, argument = null) => {
      const editor = editorRef.current;
      if (!editor) return;

      editor.focus();
      document.execCommand(command, false, argument);
      updateActiveFormats();

      isTypingRef.current = true;
      const markdown = htmlToMarkdown(editor.innerHTML);
      setIsEmpty(!editor.innerText.trim());
      if (onChange) {
        onChange(markdown);
      }
      setTimeout(() => {
        isTypingRef.current = false;
      }, 50);
    },
    [onChange, updateActiveFormats]
  );

  // Toggle heading or revert back to paragraph
  const handleToggleHeading = useCallback(
    (tag) => {
      const editor = editorRef.current;
      if (!editor) return;

      editor.focus();
      const selection = window.getSelection();
      let isCurrentTag = false;

      if (selection && selection.rangeCount > 0) {
        let node = selection.anchorNode;
        if (node && node.nodeType === Node.TEXT_NODE) {
          node = node.parentElement;
        }
        if (node && editor.contains(node)) {
          const heading = node.closest(tag.toLowerCase());
          if (heading) {
            isCurrentTag = true;
          }
        }
      }

      if (isCurrentTag) {
        executeCommand("formatBlock", "<p>");
      } else {
        executeCommand("formatBlock", `<${tag}>`);
      }
    },
    [executeCommand]
  );

  const handleInput = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    isTypingRef.current = true;
    const textContent = editor.innerText || "";
    setIsEmpty(!textContent.trim());

    const markdown = htmlToMarkdown(editor.innerHTML);
    if (onChange) {
      onChange(markdown);
    }

    updateActiveFormats();

    setTimeout(() => {
      isTypingRef.current = false;
    }, 50);
  }, [onChange, updateActiveFormats]);

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && !e.shiftKey) {
      if (e.key === "b" || e.key === "B") {
        e.preventDefault();
        executeCommand("bold");
      } else if (e.key === "i" || e.key === "I") {
        e.preventDefault();
        executeCommand("italic");
      } else if (e.key === "k" || e.key === "K") {
        e.preventDefault();
        saveSelection();
        setShowLinkInput((prev) => !prev);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    if (!text) return;

    if (text.includes("\n") || /^[-*#]/.test(text.trim())) {
      const html = markdownToHtml(text);
      document.execCommand("insertHTML", false, html);
    } else {
      document.execCommand("insertText", false, text);
    }
    handleInput();
  };

  const applyLink = () => {
    if (linkUrl.trim()) {
      let finalUrl = linkUrl.trim();
      if (!/^https?:\/\//i.test(finalUrl)) {
        finalUrl = `https://${finalUrl}`;
      }
      restoreSelection();
      executeCommand("createLink", finalUrl);
      setLinkUrl("");
      setShowLinkInput(false);
    }
  };

  return (
    <div
      className={`border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden transition-all bg-white dark:bg-gray-800 outline-none focus-within:outline-none ${className}`}
    >
      {/* In-Place Formatting Toolbar */}
      <div className="flex items-center flex-wrap gap-1 px-3 py-2 border-b border-gray-100 dark:border-gray-700/80 bg-gray-50/70 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 select-none">
        {/* Bold */}
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            executeCommand("bold");
          }}
          className={`p-1.5 rounded-lg transition-colors ${
            activeFormats.bold
              ? "bg-teal-50 dark:bg-teal-950/40 text-[#00B4A6] dark:text-[#05DAC3] font-bold"
              : "hover:bg-gray-200/70 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          }`}
          title="Bold (Ctrl+B)"
        >
          <LuBold className="w-4 h-4" />
        </button>

        {/* Italic */}
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            executeCommand("italic");
          }}
          className={`p-1.5 rounded-lg transition-colors ${
            activeFormats.italic
              ? "bg-teal-50 dark:bg-teal-950/40 text-[#00B4A6] dark:text-[#05DAC3] font-bold"
              : "hover:bg-gray-200/70 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          }`}
          title="Italic (Ctrl+I)"
        >
          <LuItalic className="w-4 h-4" />
        </button>

        <span className="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-1" />

        {/* Heading 1 */}
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            handleToggleHeading("h1");
          }}
          className={`p-1.5 rounded-lg transition-colors ${
            activeFormats.h1
              ? "bg-teal-50 dark:bg-teal-950/40 text-[#00B4A6] dark:text-[#05DAC3] font-bold"
              : "hover:bg-gray-200/70 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          }`}
          title="Heading 1"
        >
          <LuHeading1 className="w-4 h-4" />
        </button>

        {/* Heading 2 */}
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            handleToggleHeading("h2");
          }}
          className={`p-1.5 rounded-lg transition-colors ${
            activeFormats.h2
              ? "bg-teal-50 dark:bg-teal-950/40 text-[#00B4A6] dark:text-[#05DAC3] font-bold"
              : "hover:bg-gray-200/70 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          }`}
          title="Heading 2"
        >
          <LuHeading2 className="w-4 h-4" />
        </button>

        {/* Heading 3 */}
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            handleToggleHeading("h3");
          }}
          className={`p-1.5 rounded-lg transition-colors ${
            activeFormats.h3
              ? "bg-teal-50 dark:bg-teal-950/40 text-[#00B4A6] dark:text-[#05DAC3] font-bold"
              : "hover:bg-gray-200/70 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          }`}
          title="Heading 3"
        >
          <LuHeading3 className="w-4 h-4" />
        </button>

        <span className="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-1" />

        {/* Bullet List */}
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            executeCommand("insertUnorderedList");
          }}
          className={`p-1.5 rounded-lg transition-colors ${
            activeFormats.ul
              ? "bg-teal-50 dark:bg-teal-950/40 text-[#00B4A6] dark:text-[#05DAC3] font-bold"
              : "hover:bg-gray-200/70 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          }`}
          title="Bullet List"
        >
          <LuList className="w-4 h-4" />
        </button>

        {/* Numbered List */}
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            executeCommand("insertOrderedList");
          }}
          className={`p-1.5 rounded-lg transition-colors ${
            activeFormats.ol
              ? "bg-teal-50 dark:bg-teal-950/40 text-[#00B4A6] dark:text-[#05DAC3] font-bold"
              : "hover:bg-gray-200/70 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          }`}
          title="Numbered List"
        >
          <LuListOrdered className="w-4 h-4" />
        </button>

        <span className="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-1" />

        {/* Insert Link */}
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            saveSelection();
            setShowLinkInput((prev) => !prev);
          }}
          className={`p-1.5 rounded-lg transition-colors ${
            showLinkInput
              ? "bg-teal-50 dark:bg-teal-950/40 text-[#00B4A6] dark:text-[#05DAC3] font-bold"
              : "hover:bg-gray-200/70 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          }`}
          title="Insert Link (Ctrl+K)"
        >
          <LuLink className="w-4 h-4" />
        </button>

        {/* Inline Link Prompt */}
        {showLinkInput && (
          <div className="flex items-center gap-1.5 ml-2 bg-white dark:bg-gray-700 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-600 shadow-xs">
            <input
              type="text"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              className="text-xs bg-transparent border-none outline-hidden text-gray-900 dark:text-white w-44 placeholder-gray-400"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyLink();
                } else if (e.key === "Escape") {
                  setShowLinkInput(false);
                  setLinkUrl("");
                }
              }}
            />
            <button
              type="button"
              onClick={applyLink}
              className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 p-0.5 rounded"
              title="Apply link"
            >
              <LuCheck className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => {
                setShowLinkInput(false);
                setLinkUrl("");
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-0.5 rounded"
              title="Cancel"
            >
              <LuX className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Single In-Place Rich Surface: Direct Visual Preview */}
      <div className="relative">
        {isEmpty && (
          <div className="absolute top-3.5 left-4 text-sm text-gray-400 dark:text-gray-500 pointer-events-none select-none">
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onKeyUp={updateActiveFormats}
          onMouseUp={updateActiveFormats}
          style={{ minHeight: `${height}px`, maxHeight: "360px" }}
          className="p-4 text-sm sm:text-base text-gray-900 dark:text-gray-100 outline-none focus:outline-none focus:ring-0 overflow-y-auto leading-relaxed prose prose-sm dark:prose-invert max-w-none prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:my-1 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-blockquote:my-2 prose-blockquote:border-l-4 prose-blockquote:border-[#05DAC3] prose-blockquote:bg-teal-50/50 dark:prose-blockquote:bg-teal-950/20 prose-blockquote:py-1 prose-blockquote:px-3 prose-blockquote:rounded-r-md prose-blockquote:not-italic prose-a:text-[#00B4A6] dark:prose-a:text-[#05DAC3]"
        />
      </div>
    </div>
  );
}

export { MarkdownEditor };
